import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { FiltersBarDropdownButton } from "app/components/filters-bar/filters-bar-dropdown/FiltersBarDropdownButton";
import { FILTER_CHANGED_NOTIFICATION_WIDTH } from "app/components/filters-bar/FiltersBase";
import { AddOnType } from "app/contexts/AddonStateContext/const";
import { PulseDot } from "app/components/pulse-dot/PulseDot";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import type { Presets } from "app/components/duration-selector/DurationSelectorSimple";
import { getDropDownButtonTitle } from "app/components/duration-selector/DurationSelectorUtils";
import { useTotalVisitsComponentHistoryHook } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/TotalVisitsComponentHistoryHook";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import type { IndicatorType } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import { IndicatorTypes } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import FilterChangedNotification from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltip";
import { EFilterChangedTooltipTypes } from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipTypes";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

const DurationSelectorTextStyle = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
`;

const DurationSelectorIconStyle = styled.div`
    width: 24px;
    height: 24px;
    margin-right: 8px;
`;

const DurationSelectorDropdownButton = styled(FiltersBarDropdownButton)`
    & .DropdownButton-text.DropdownButton-text {
        text-indent: 0;
        margin-right: 15px;
        width: 100%;
    }
`;

interface IDurationSelectorButton {
    height?: number | string;
    isDisabled: boolean;
    initialPreset: string;
    presets?: Presets;
    isCompare?: boolean | string;
    presentNotificationIndicator?: boolean;
    presentCustomIndicator?: boolean;
    allowSingleDayPresentation?: boolean;
    presentNotificationTooltipKey?: string;
    indicatorType?: IndicatorType;
}

export const DurationSelectorButton: FC<IDurationSelectorButton> = ({
    height,
    isDisabled,
    initialPreset,
    presets,
    isCompare = false,
    presentNotificationIndicator = false,
    presentCustomIndicator = true,
    allowSingleDayPresentation = false,
    presentNotificationTooltipKey = "common.filters.duration.tooltip.body",
    indicatorType = IndicatorTypes.NONE,
}) => {
    const WEBSITE_ANALYSIS = "digitalsuite_website";
    const PAGES_TO_SHOW_PULSE_INDICATOR = [
        "digitalsuite_website_trafficandengagement",
        "digitalsuite_website_websiteperformance",
    ];
    const {
        state: { name: pageName, parent },
    } = useCurrentStateAndParams();

    const translate = useTranslation();
    const body = translate(presentNotificationTooltipKey);
    const isPulsingIndicatorEnabled = GrowthbookService.growthbookInstance.isOn(TestsNames.TOTAL_VISITS_HOOK);

    const { isPulsarShown: isMajorPeek } = useTotalVisitsComponentHistoryHook();
    const { getIsAddonAvailable } = useAddonsState();
    const isCurrentPageAllowsToShowPulse =
        // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — pageName comes from useCurrentStateAndParams (external @uirouter/react), includes tolerates undefined
        parent === WEBSITE_ANALYSIS && PAGES_TO_SHOW_PULSE_INDICATOR.includes(pageName);

    const isPulseShown =
        indicatorType === IndicatorTypes.PULSE &&
        isCurrentPageAllowsToShowPulse &&
        isPulsingIndicatorEnabled &&
        isMajorPeek;

    return (
        <DurationSelectorDropdownButton
            disabled={isDisabled}
            hasValue={true}
            height={height}
        >
            <DurationSelectorIconStyle>
                <SWReactIcons iconName="daily-ranking" />
            </DurationSelectorIconStyle>
            <DurationSelectorTextStyle>
                {getDropDownButtonTitle(initialPreset, presets, presentCustomIndicator, allowSingleDayPresentation)}
            </DurationSelectorTextStyle>
            {presentNotificationIndicator ? (
                <>
                    {indicatorType === IndicatorTypes.DOT ? (
                        <FilterChangedNotification
                            width={FILTER_CHANGED_NOTIFICATION_WIDTH}
                            tooltipBody={body}
                            filterType={EFilterChangedTooltipTypes.DATE_PICKER}
                        />
                    ) : null}

                    {isPulseShown && getIsAddonAvailable(AddOnType.HistoricalData) ? (
                        <PulseDot
                            pulseDiameter={10}
                            padding={4}
                            centerDiameter={10}
                            color={colorsPalettes.blue[400]}
                        />
                    ) : null}
                </>
            ) : null}
        </DurationSelectorDropdownButton>
    );
};
